import { Box, Button, Divider, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon, TriangleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { useBuilderBoxApi } from '../../services/api/useBuilderBox.api';
import { BuilderAnalysisType } from '../../types/SimilarlySituated/BuilderAnalysisType.type';
import { BuilderBoxAnalysis } from '../../types/SimilarlySituated/BuilderBoxAnalysis.type';
import { BuilderStage } from '../../types/SimilarlySituated/BuilderStage.type';
import { SsbOutcome } from '../../types/SimilarlySituated/SsbOutcome.type';
import { Statute } from '../../types/Statute.type';
import { isStatuteAnalysisType } from '../../utils/ssbBuilderBox';
import { SSB_STEP_HEADERS } from '../../utils/ssbStepHeaders';
import { INSPECT_COLUMN_WIDTH } from '../../utils/ssbStyles';
import OutcomeRow from './OutcomeRow';
import RemovableChip from './RemovableChip';
import SsbSectionLabel from './SsbSectionLabel';
import SsbStepHeader from './SsbStepHeader';

interface OutcomeStepProps {
  builderBoxId: number;
  outcomes: SsbOutcome[];
  savedAnalyses?: BuilderBoxAnalysis[];
  countyId?: number | null;
  onOutcomesChange: (outcomes: SsbOutcome[]) => void;
  onRun?: (outcomes: SsbOutcome[]) => void;
  isRunning?: boolean;
  runError?: string | null;
}

interface OutcomeSectionView extends BuilderStage {
  rows: BuilderAnalysisType[];
}

interface PreviewChip {
  key: string;
  label: string;
  isInvalid: boolean;
  onRemove: () => void;
}

// stage ids grow more negative the later the stage, so sections run from the earliest stage
function buildSections(rows: BuilderAnalysisType[]): OutcomeSectionView[] {
  const sections = new Map<number, OutcomeSectionView>();

  rows.forEach((row) => {
    const stage = row.builder_analysis_type_stage;
    if (!sections.has(stage.id)) {
      sections.set(stage.id, { ...stage, rows: [] });
    }
    sections.get(stage.id)?.rows.push(row);
  });

  return [...sections.values()].sort((a, b) => b.id - a.id);
}

function statuteOutcomeLabel(outcomeName: string, statute: Statute): string {
  return `${outcomeName.replace(/\s*(…|\.\.\.)\s*$/, '')} ${statute.display_name}`;
}

const analysisKey = (typeId: number, statuteId?: number) => `${typeId}:${statuteId ?? ''}`;

function OutcomeStep({
  builderBoxId,
  outcomes,
  savedAnalyses = [],
  countyId,
  onOutcomesChange,
  onRun,
  isRunning,
  runError,
}: OutcomeStepProps) {
  const { getAnalysisTypes } = useBuilderBoxApi();
  const [editingTypeId, setEditingTypeId] = useState<number | null>(null);
  // statutes before the picker opened, restored on cancel (null = the outcome was not selected)
  const [editSnapshot, setEditSnapshot] = useState<Statute[] | null>(null);

  const { data: analysisTypes, isPending } = useQuery({
    queryKey: ['builderBoxAnalysisTypes', builderBoxId],
    queryFn: () => getAnalysisTypes(builderBoxId),
  });

  const findOutcome = (typeId: number) => outcomes.find((outcome) => outcome.analysisTypeId === typeId);

  const removeOutcome = (typeId: number) => {
    onOutcomesChange(outcomes.filter((outcome) => outcome.analysisTypeId !== typeId));
    if (editingTypeId === typeId) setEditingTypeId(null);
  };

  const ensureOutcome = (typeId: number) => {
    if (!findOutcome(typeId)) {
      onOutcomesChange([...outcomes, { analysisTypeId: typeId, statutes: [] }]);
    }
  };

  // opening another picker closes the current one, dropping its outcome if no statute was picked
  const startEditing = (typeId: number) => {
    const remaining = outcomes.filter(
      (outcome) =>
        outcome.analysisTypeId === typeId || outcome.analysisTypeId !== editingTypeId || outcome.statutes.length > 0,
    );
    setEditSnapshot(findOutcome(typeId)?.statutes ?? null);
    setEditingTypeId(typeId);
    onOutcomesChange(
      remaining.some((outcome) => outcome.analysisTypeId === typeId)
        ? remaining
        : [...remaining, { analysisTypeId: typeId, statutes: [] }],
    );
  };

  const handleToggle = (row: BuilderAnalysisType) => {
    const typeId = row.id;
    if (findOutcome(typeId)) {
      removeOutcome(typeId);
      return;
    }
    if (isStatuteAnalysisType(row)) {
      startEditing(typeId);
    } else {
      ensureOutcome(typeId);
    }
  };

  const handleCancelEditing = (typeId: number) => {
    setEditingTypeId(null);
    if (!editSnapshot?.length) {
      removeOutcome(typeId);
      return;
    }
    onOutcomesChange(
      outcomes.map((outcome) => (outcome.analysisTypeId === typeId ? { ...outcome, statutes: editSnapshot } : outcome)),
    );
  };

  const handleStatutesChange = (typeId: number, statutes: Statute[]) => {
    if (statutes.length === 0 && editingTypeId !== typeId) {
      removeOutcome(typeId);
      return;
    }
    onOutcomesChange(
      outcomes.map((outcome) => (outcome.analysisTypeId === typeId ? { ...outcome, statutes } : outcome)),
    );
  };

  const handleStatuteSelected = (typeId: number, statutes: Statute[]) => {
    handleStatutesChange(typeId, statutes);
    setEditingTypeId(null);
  };

  const catalogTypesById = new Map(analysisTypes?.map((type) => [type.id, type]));
  // a rebuilt box can drop outcomes from the catalog, their saved analyses still name them so they can be removed
  const typesById = new Map([
    ...savedAnalyses.map((analysis) => [analysis.builder_analysis_type.id, analysis.builder_analysis_type] as const),
    ...catalogTypesById,
  ]);
  const invalidKeys = new Set(
    savedAnalyses
      .filter((analysis) => !analysis.is_valid)
      .map((analysis) => analysisKey(analysis.builder_analysis_type.id, analysis.statute?.id)),
  );
  // a statute outcome whose picker is still open may hold no statute yet
  const runnableOutcomes = outcomes.filter((outcome) => {
    const type = catalogTypesById.get(outcome.analysisTypeId);
    return type && (!isStatuteAnalysisType(type) || outcome.statutes.length > 0);
  });

  const previewChips: PreviewChip[] = outcomes.flatMap((outcome) => {
    const type = typesById.get(outcome.analysisTypeId);
    if (!type) return [];

    if (!isStatuteAnalysisType(type)) {
      return [
        {
          key: `${type.id}`,
          label: type.name,
          isInvalid: invalidKeys.has(analysisKey(type.id)),
          onRemove: () => removeOutcome(type.id),
        },
      ];
    }

    return outcome.statutes.map((statute) => ({
      key: `${type.id}-${statute.id}`,
      label: statuteOutcomeLabel(type.name, statute),
      isInvalid: invalidKeys.has(analysisKey(type.id, statute.id)),
      onRemove: () =>
        handleStatutesChange(
          type.id,
          outcome.statutes.filter((s) => s.id !== statute.id),
        ),
    }));
  });

  const renderSections = () => {
    if (isPending) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[0, 1, 2, 3, 4].map((index) => (
            <Skeleton key={index} variant='rounded' height={56} sx={{ borderRadius: '8px' }} />
          ))}
        </Box>
      );
    }

    if (!analysisTypes?.length) {
      return (
        <Typography sx={{ fontSize: '13px', color: '#718096' }}>
          The outcome catalog could not be loaded for this box.
        </Typography>
      );
    }

    return buildSections(analysisTypes).map((section) => (
      <Box key={section.id} sx={{ marginBottom: '24px' }}>
        <SsbSectionLabel sx={{ color: '#1A365D', marginBottom: '12px' }}>{section.name}</SsbSectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {section.rows.map((row) => (
            <OutcomeRow
              key={row.id}
              outcomeType={row}
              outcome={findOutcome(row.id)}
              countyId={countyId}
              isEditing={editingTypeId === row.id}
              onToggle={() => handleToggle(row)}
              onAdd={() => startEditing(row.id)}
              onCancelEditing={() => handleCancelEditing(row.id)}
              onStatuteSelected={(statutes) => handleStatuteSelected(row.id, statutes)}
              onStatutesChange={(statutes) => handleStatutesChange(row.id, statutes)}
            />
          ))}
        </Box>
      </Box>
    ));
  };

  return (
    <Box sx={{ paddingBottom: '32px' }}>
      <SsbStepHeader {...SSB_STEP_HEADERS.Analyze} />
      <Box sx={{ display: 'flex', gap: '20px' }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {renderSections()}

          <Divider sx={{ backgroundColor: '#EDF1F6', marginTop: '48px', marginBottom: '24px' }} />

          <SsbSectionLabel sx={{ color: '#4A5568', marginBottom: '12px' }}>Selected Analyses</SsbSectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px' }}>
            {previewChips.map((chip) => (
              <RemovableChip
                key={chip.key}
                variant={chip.isInvalid ? 'error' : 'outlined'}
                label={chip.label}
                onRemove={chip.onRemove}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              endIcon={<ArrowRightIcon size={16} />}
              disabled={!onRun || runnableOutcomes.length === 0 || isRunning || Boolean(runError)}
              onClick={() => onRun?.(runnableOutcomes)}
              sx={{ paddingX: 4 }}
            >
              Run
            </Button>
            {runError && (
              <Box role='alert' sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <TriangleAlertIcon size={30} fill='#C53030' stroke='#EDF2F7' />
                <Typography sx={{ fontSize: '12px', color: '#533333', fontWeight: 600, maxWidth: '600px' }}>
                  {runError}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ width: INSPECT_COLUMN_WIDTH, flexShrink: 0 }} />
      </Box>
    </Box>
  );
}

export default OutcomeStep;
